import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Card,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Stack,
  Divider,
  Alert,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  PeopleAltRounded as PeopleIcon,
  EventRounded as EventIcon,
  FactCheckRounded as AssessmentIcon,
  QueryStatsRounded as QueryStatsIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  PersonAddAltRounded as PersonAddIcon,
  EventAvailableRounded as EventAddIcon,
  MailOutlineRounded as EmailIcon,
  PhoneRounded as PhoneIcon,
} from '@mui/icons-material';
import { format, subMonths, startOfMonth, startOfDay } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import customerService from '../services/customerService';
import appointmentService from '../services/appointmentService';
import assessmentService from '../services/assessmentService';
import { useAuth } from '../hooks/useAuth';
import StatCard from '../components/ui/StatCard';
import EmptyState from '../components/ui/EmptyState';
import LoadingState from '../components/ui/LoadingState';
import StatusChip from '../components/ui/StatusChip';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SOURCE_LABEL = {
  website: 'Website',
  contact: 'Contact form',
  assessment: 'Assessment',
  appointment: 'Appointment',
  other: 'Other',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalAppointments: 0,
    totalAssessments: 0,
    recentContacts: [],
    upcomingAppointments: [],
    customerTrend: 0,
    appointmentTrend: 0,
    assessmentTrend: 0,
    conversionRate: 0,
    conversionTrend: 0,
    sourcesData: [],
  });

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all data
        const [customers, appointments, assessments] = await Promise.all([
          customerService.getCustomers(),
          appointmentService.getAppointments(),
          assessmentService.getAssessments().catch(() => []), // This might not be implemented yet
        ]);

        // Date ranges for trend analysis. To avoid comparing a partial current
        // month against a complete previous month, the "last month" window is
        // cut off at the same point in time one month back (month-to-date vs
        // month-to-date).
        const now = new Date();
        const currentMonthStart = startOfMonth(now);
        const lastMonthStart = startOfMonth(subMonths(now, 1));
        const sameTimeLastMonth = subMonths(now, 1);

        const inWindow = (value, start, end) => {
          const date = new Date(value);
          return date >= start && date <= end;
        };

        const currentMonthCustomers = customers.filter((customer) =>
          inWindow(customer.createdAt, currentMonthStart, now)
        );
        const lastMonthCustomers = customers.filter((customer) =>
          inWindow(customer.createdAt, lastMonthStart, sameTimeLastMonth)
        );

        const currentMonthAppointments = appointments.filter((appointment) =>
          inWindow(appointment.createdAt, currentMonthStart, now)
        );
        const lastMonthAppointments = appointments.filter((appointment) =>
          inWindow(appointment.createdAt, lastMonthStart, sameTimeLastMonth)
        );

        const currentMonthAssessments = assessments.filter((assessment) =>
          inWindow(assessment.createdAt, currentMonthStart, now)
        );
        const lastMonthAssessments = assessments.filter((assessment) =>
          inWindow(assessment.createdAt, lastMonthStart, sameTimeLastMonth)
        );

        // Calculate trends (percentage change from last month)
        const calculateTrend = (current, previous) => {
          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / previous) * 100;
        };

        const customerTrend = calculateTrend(currentMonthCustomers.length, lastMonthCustomers.length);
        const appointmentTrend = calculateTrend(
          currentMonthAppointments.length,
          lastMonthAppointments.length
        );
        const assessmentTrend = calculateTrend(
          currentMonthAssessments.length,
          lastMonthAssessments.length
        );

        // Conversion rate: what % of customers have at least one appointment?
        // appointment.customer may be a populated object or a bare id.
        const appointmentCustomerId = (appointment) =>
          appointment.customer?._id || appointment.customer;

        const customersWithAppointments = new Set();
        appointments.forEach((appointment) => {
          const customerId = appointmentCustomerId(appointment);
          if (customerId) {
            customersWithAppointments.add(String(customerId));
          }
        });

        const conversionRate =
          customers.length > 0 ? (customersWithAppointments.size / customers.length) * 100 : 0;

        // Trend compares like with like: conversion within this month's new
        // customers vs conversion within last month's new customers.
        const cohortConversion = (cohort) => {
          if (cohort.length === 0) return 0;
          const converted = cohort.filter((customer) =>
            customersWithAppointments.has(String(customer._id))
          ).length;
          return (converted / cohort.length) * 100;
        };

        const conversionTrend = calculateTrend(
          cohortConversion(currentMonthCustomers),
          cohortConversion(lastMonthCustomers)
        );

        // Customer sources for chart
        const sourceCounts = {
          website: 0,
          contact: 0,
          assessment: 0,
          appointment: 0,
          other: 0,
        };

        customers.forEach((customer) => {
          const source = customer.source || 'other';
          if (sourceCounts.hasOwnProperty(source)) {
            sourceCounts[source]++;
          } else {
            sourceCounts.other++;
          }
        });

        const sourcesData = {
          labels: Object.keys(sourceCounts).map((key) => SOURCE_LABEL[key] || key),
          data: Object.values(sourceCounts),
        };

        // Recent contacts (last 7 days)
        const recentContacts = customers
          .filter((customer) => {
            const createdAt = new Date(customer.createdAt);
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            return createdAt >= sevenDaysAgo;
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);

        // Upcoming appointments — compare against the start of today so that
        // appointments later today still show up.
        const todayStart = startOfDay(new Date());
        const upcomingAppointments = appointments
          .filter((appointment) => {
            const appointmentDate = new Date(appointment.date);
            return appointmentDate >= todayStart && appointment.status === 'scheduled';
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 5);

        setStats({
          totalCustomers: customers.length,
          totalAppointments: appointments.length,
          totalAssessments: assessments.length,
          recentContacts,
          upcomingAppointments,
          customerTrend,
          appointmentTrend,
          assessmentTrend,
          conversionRate,
          conversionTrend,
          sourcesData,
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const chartData = useMemo(() => {
    const brand = theme.palette.primary.main;
    return {
      labels: stats.sourcesData.labels || [],
      datasets: [
        {
          label: 'Customers',
          data: stats.sourcesData.data || [],
          backgroundColor: [
            alpha(brand, 0.9),
            alpha(brand, 0.7),
            alpha(brand, 0.55),
            alpha(brand, 0.4),
            alpha(theme.palette.text.secondary, 0.3),
          ],
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 42,
        },
      ],
    };
  }, [stats.sourcesData, theme]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: theme.palette.mode === 'light' ? '#0F172A' : '#E2E8F0',
          titleColor: theme.palette.mode === 'light' ? '#fff' : '#0F172A',
          bodyColor: theme.palette.mode === 'light' ? '#CBD5E1' : '#334155',
          cornerRadius: 10,
          padding: 12,
          displayColors: false,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: theme.palette.text.secondary, font: { size: 11 } },
          border: { display: false },
        },
        y: {
          beginAtZero: true,
          grid: { color: alpha(theme.palette.text.primary, 0.06) },
          ticks: { color: theme.palette.text.secondary, precision: 0, font: { size: 11 } },
          border: { display: false },
        },
      },
    }),
    [theme]
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name ? user.name.split(' ')[0] : 'there';

  if (loading) {
    return <LoadingState label="Loading your dashboard…" />;
  }

  return (
    <Box>
      {/* Greeting hero */}
      <Box
        sx={{
          mb: { xs: 2.5, md: 3.5 },
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: 'primary.main' }}>
            {format(new Date(), 'EEEE, MMMM d')}
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
            {greeting}, {firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Here's what's happening across Skyline today.
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1.25}
          useFlexGap
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' }, whiteSpace: 'nowrap' },
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<EventAddIcon />}
            onClick={() => navigate('/appointments/new')}
            sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            New Appointment
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/customers/new')}
          >
            Add Customer
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      <Grid container spacing={{ xs: 1.5, md: 2.5 }} sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Customers"
            value={stats.totalCustomers}
            icon={<PeopleIcon />}
            color="primary"
            trend={stats.customerTrend}
            trendLabel="vs same time last month"
            onClick={() => navigate('/customers')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Appointments"
            value={stats.totalAppointments}
            icon={<EventIcon />}
            color="info"
            trend={stats.appointmentTrend}
            trendLabel="vs same time last month"
            onClick={() => navigate('/appointments')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Assessments"
            value={stats.totalAssessments}
            icon={<AssessmentIcon />}
            color="success"
            trend={stats.assessmentTrend}
            trendLabel="vs same time last month"
            onClick={() => navigate('/assessments')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Conversion"
            value={`${Math.round(stats.conversionRate)}%`}
            icon={<QueryStatsIcon />}
            color="warning"
            trend={stats.conversionTrend}
            trendLabel="new customers, vs last month"
          />
        </Grid>
      </Grid>

      {/* Main content */}
      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        {/* Recent Contacts */}
        <Grid item xs={12} md={6} lg={5}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}
            >
              <Box>
                <Typography variant="h6">Recent Contacts</Typography>
                <Typography variant="caption" color="text.secondary">
                  New leads from the last 7 days
                </Typography>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/customers')}
              >
                View all
              </Button>
            </Stack>
            <Divider />
            {stats.recentContacts.length > 0 ? (
              <List sx={{ p: 1, flex: 1 }}>
                {stats.recentContacts.map((contact) => (
                  <ListItemButton
                    key={contact._id}
                    onClick={() => navigate(`/customers/${contact._id}`)}
                    sx={{ borderRadius: '12px', mb: 0.5, alignItems: 'flex-start', gap: 0.5 }}
                  >
                    <ListItemAvatar sx={{ minWidth: 52 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.14),
                          color: 'primary.main',
                          fontWeight: 700,
                        }}
                      >
                        {(contact.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="subtitle2" noWrap>
                            {contact.name}
                          </Typography>
                          <StatusChip
                            status={contact.source || 'website'}
                            color={contact.source === 'assessment' ? 'success' : 'info'}
                            label={SOURCE_LABEL[contact.source] || 'Website'}
                          />
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                            <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {contact.email || '—'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {contact.phone || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important' }}>
                              {format(new Date(contact.createdAt), 'MMM d')}
                            </Typography>
                          </Stack>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <EmptyState
                icon={<PeopleIcon />}
                title="No recent contacts"
                description="New leads from the website, contact forms, and assessments will appear here."
                sx={{ py: 5 }}
              />
            )}
          </Card>
        </Grid>

        {/* Customer Sources Chart */}
        <Grid item xs={12} md={6} lg={7}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}>
              <Typography variant="h6">Customer Sources</Typography>
              <Typography variant="caption" color="text.secondary">
                Where your customers come from
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ p: 2.5, flex: 1, minHeight: { xs: 260, md: 320 } }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        {/* Upcoming Appointments */}
        <Grid item xs={12}>
          <Card>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}
            >
              <Box>
                <Typography variant="h6">Upcoming Appointments</Typography>
                <Typography variant="caption" color="text.secondary">
                  Next scheduled visits
                </Typography>
              </Box>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/appointments')}
              >
                View all
              </Button>
            </Stack>
            <Divider />
            {stats.upcomingAppointments.length > 0 ? (
              <List sx={{ p: 1 }}>
                {stats.upcomingAppointments.map((appointment) => {
                  const date = new Date(appointment.date);
                  return (
                    <ListItemButton
                      key={appointment._id}
                      onClick={() => navigate(`/appointments/${appointment._id}`)}
                      sx={{ borderRadius: '12px', mb: 0.5, gap: 1.75 }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 52,
                          height: 56,
                          borderRadius: '14px',
                          flexShrink: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: 'info.main',
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1 }}>
                          {format(date, 'd')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', mt: 0.35 }}>
                          {format(date, 'MMM').toUpperCase()}
                        </Typography>
                      </Box>
                      <ListItemText
                        disableTypography
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            justifyContent="space-between"
                          >
                            <Typography variant="subtitle2" noWrap>
                              {appointment.customer?.name || 'Customer'}
                            </Typography>
                            <StatusChip
                              status={appointment.serviceType || 'Inspection'}
                              color="info"
                              label={appointment.serviceType || 'Inspection'}
                            />
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.25 }}>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {format(date, 'EEEE, MMMM d')} · {appointment.timeSlot || 'Time TBD'}
                            </Typography>
                            {appointment.notes && (
                              <Typography variant="caption" color="text.disabled" noWrap sx={{ display: 'block' }}>
                                {appointment.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            ) : (
              <EmptyState
                icon={<EventIcon />}
                title="No upcoming appointments"
                description="Scheduled appointments will appear here. Create one to get started."
                color="info"
                action={
                  <Button variant="contained" onClick={() => navigate('/appointments/new')}>
                    Schedule Appointment
                  </Button>
                }
                sx={{ py: 5 }}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
